import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ChatHeader } from './ChatHeader';
import { ChatMessages } from './ChatMessages';
import { ChatInput } from './ChatInput';
import * as chatService from '../../services/chatService';
import type { ChatMessage } from '../../services/chatService';

interface ChatWidgetProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onCreateRequirement?: (requirementData: { title: string; description: string; requirement_type: string; priority: string }) => void;
}

export function ChatWidget({ projectId, isOpen, onClose, onCreateRequirement }: ChatWidgetProps): React.ReactElement | null {
  const { token, user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokensUsed, setTokensUsed] = useState(0);
  const [tokenLimit, setTokenLimit] = useState(10000);

  // Get user initials for messages
  const userInitials = user
    ? `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`.toUpperCase() || 'U'
    : 'U';

  // Create or load session when widget opens
  useEffect(() => {
    if (isOpen && token && projectId && !sessionId) {
      initializeSession();
    }
  }, [isOpen, token, projectId, sessionId]);

  const initializeSession = async () => {
    if (!token) return;

    try {
      setIsLoading(true);
      setError('');

      // Try to get existing sessions first
      const sessions = await chatService.getProjectSessions(projectId, token);

      if (sessions.length > 0) {
        // Use the most recent active session
        const latestSession = sessions[0];
        setSessionId(latestSession.id);
        setTokensUsed(latestSession.total_tokens_used);

        // Load messages for this session
        const sessionData = await chatService.getSession(projectId, latestSession.id, token);
        setMessages(sessionData.messages);
      } else {
        // Create a new session
        const newSession = await chatService.createSession(
          projectId,
          { session_name: 'Requirements Assistant Chat' },
          token
        );
        setSessionId(newSession.id);
        setTokensUsed(0);
      }
    } catch (err) {
      console.error('Failed to initialize chat session:', err);
      setError('Failed to start chat session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (message: string) => {
    if (!token || !sessionId) {
      setError('Chat session not initialized. Please try reopening the chat.');
      return;
    }

    // Add user message to UI immediately
    const userMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      session_id: sessionId,
      user_id: user?.id || null,
      message_type: 'user',
      message_content: message,
      tokens_used: null,
      action_type: null,
      action_status: null,
      action_data: null,
      requirement_id: null,
      created_at: new Date().toISOString(),
      is_active: true
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError('');

    try {
      // Send message to backend
      const response = await chatService.sendMessage(
        projectId,
        {
          sessionId,
          message
        },
        token
      );

      // Add AI response to messages
      const aiMessage: ChatMessage = {
        id: response.messageId,
        session_id: sessionId,
        user_id: null,
        message_type: 'ai',
        message_content: response.aiResponse,
        tokens_used: response.tokensUsed,
        action_type: response.action?.type || null,
        action_status: response.action?.status || null,
        action_data: response.action ? JSON.stringify(response.action.data) : null,
        requirement_id: null,
        created_at: new Date().toISOString(),
        is_active: true
      };

      setMessages(prev => [...prev, aiMessage]);
      setTokensUsed(response.totalTokensUsed);
      setTokenLimit(response.tokenLimit);

      // Debug logging for action detection
      console.log('=== ACTION DEBUG ===');
      console.log('response.action:', response.action);
      console.log('response.action?.type:', response.action?.type);
      console.log('response.action?.status:', response.action?.status);
      console.log('response.action?.data:', response.action?.data);
      console.log('response.action?.data?.data:', response.action?.data?.data);
      console.log('onCreateRequirement exists:', !!onCreateRequirement);
      console.log('===================');

      // Auto-trigger requirement modal for create_requirement actions (only for NEW messages)
      if (
        response.action?.type === 'create_requirement' &&
        response.action?.status === 'pending' &&
        response.action?.data?.data &&
        onCreateRequirement
      ) {
        // Extract requirement data from action (ActionData has nested data property)
        const reqData = response.action.data.data;
        const requirementData = {
          title: reqData.title || '',
          description: reqData.description || '',
          requirement_type: reqData.requirement_type || 'functional',
          priority: reqData.priority || 'medium'
        };

        console.log('✅ Auto-opening requirement modal with data from NEW AI message:', requirementData);
        onCreateRequirement(requirementData);
      } else {
        console.log('❌ Modal NOT triggered. Condition check failed.');
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message. Please try again.');

      // Add error system message
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        session_id: sessionId,
        user_id: null,
        message_type: 'system',
        message_content: '❌ Failed to send message. Please try again.',
        tokens_used: null,
        action_type: null,
        action_status: null,
        action_data: null,
        requirement_id: null,
        created_at: new Date().toISOString(),
        is_active: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveAction = async (messageId: string) => {
    if (!token) {
      setError('Not authenticated');
      return;
    }

    try {
      await chatService.approveAction(
        projectId,
        { messageId },
        token
      );

      // Update the message status to approved
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? { ...msg, action_status: 'approved' as const }
          : msg
      ));

      // Add success message
      const successMessage: ChatMessage = {
        id: `success-${Date.now()}`,
        session_id: sessionId!,
        user_id: null,
        message_type: 'system',
        message_content: '✓ Action approved and executed successfully! Refreshing page...',
        tokens_used: null,
        action_type: null,
        action_status: null,
        action_data: null,
        requirement_id: null,
        created_at: new Date().toISOString(),
        is_active: true
      };
      setMessages(prev => [...prev, successMessage]);

      // Reload the page after a short delay to show the new requirement
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error('Failed to approve action:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve action');
    }
  };

  const handleRejectAction = async (messageId: string) => {
    if (!token) {
      setError('Not authenticated');
      return;
    }

    try {
      await chatService.rejectAction(
        projectId,
        { messageId },
        token
      );

      // Update the message status to rejected
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? { ...msg, action_status: 'rejected' as const }
          : msg
      ));

      // Add info message
      const infoMessage: ChatMessage = {
        id: `info-${Date.now()}`,
        session_id: sessionId!,
        user_id: null,
        message_type: 'system',
        message_content: 'Action rejected.',
        tokens_used: null,
        action_type: null,
        action_status: null,
        action_data: null,
        requirement_id: null,
        created_at: new Date().toISOString(),
        is_active: true
      };
      setMessages(prev => [...prev, infoMessage]);
    } catch (err) {
      console.error('Failed to reject action:', err);
      setError(err instanceof Error ? err.message : 'Failed to reject action');
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="fixed bottom-24 right-6 w-[550px] h-[600px] bg-white rounded-lg shadow-2xl flex flex-col z-40 border border-gray-200">
      <ChatHeader
        title="Requirements Assistant"
        onClose={onClose}
        tokensUsed={tokensUsed}
        tokenLimit={tokenLimit}
      />

      {error && (
        <div className="mx-4 mt-4 p-3 rounded-md bg-red-50 border border-red-200">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <ChatMessages
        messages={messages}
        userInitials={userInitials}
        isLoading={isLoading}
        onApproveAction={handleApproveAction}
        onRejectAction={handleRejectAction}
        onCreateRequirement={onCreateRequirement}
      />

      <ChatInput
        onSendMessage={handleSendMessage}
        disabled={isLoading || !sessionId}
        placeholder={sessionId ? "Ask me anything about requirements..." : "Initializing..."}
      />
    </div>
  );
}

export default ChatWidget;
